import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Utiliser la même URL backend que le reste de l'application
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dashboard de Monitoring Intégré - Type Grafana
 * Interface professionnelle pour surveiller la santé du système
 * Prévention proactive des bugs récurrents de dashboard
 */
const MonitoringDashboard = () => {
  const [systemHealth, setSystemHealth] = useState(null);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('health');
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Configuration des onglets
  const tabs = [
    { id: 'health', label: '🏥 Santé Système', icon: '📊' },
    { id: 'dashboard', label: '📈 Métriques Dashboard', icon: '🎯' },
    { id: 'alerts', label: '🚨 Alertes', icon: '' },
    { id: 'logs', label: '📋 Logs Système', icon: '📝' },
    { id: 'performance', label: '⚡ Performance', icon: '🚀' }
  ];

  // Headers d'authentification
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Chargement des données de santé système
  const loadSystemHealth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitoring/health`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setSystemHealth(response.data);
    } catch (error) {
      console.error('Erreur chargement santé système:', error);
      setError('Impossible de charger les données de santé');
    }
  };

  // Chargement des métriques dashboard
  const loadDashboardMetrics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitoring/metrics/dashboard`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setDashboardMetrics(response.data);
    } catch (error) {
      console.error('Erreur chargement métriques dashboard:', error);
      setError('Impossible de charger les métriques dashboard');
    }
  };

  // Chargement des alertes
  const loadAlerts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitoring/alerts?limit=20`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
      setError('Impossible de charger les alertes');
    }
  };

  // Chargement des logs
  const loadLogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitoring/logs?limit=50`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Erreur chargement logs:', error);
      setError('Impossible de charger les logs');
    }
  };

  // Chargement des données de performance
  const loadPerformanceData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitoring/performance?hours_back=24`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setPerformanceData(response.data);
    } catch (error) {
      console.error('Erreur chargement performance:', error);
      setError('Impossible de charger les données de performance');
    }
  };

  // Résolution d'une alerte
  const resolveAlert = async (alertId) => {
    try {
      await axios.post(`${BACKEND_URL}/monitoring/alerts/${alertId}/resolve`, {}, {
        headers: getAuthHeaders()
      });
      
      // Recharger les alertes
      await loadAlerts();
      
      // Notification de succès
      alert('Alerte résolue avec succès');
    } catch (error) {
      console.error('Erreur résolution alerte:', error);
      alert('Erreur lors de la résolution de l\'alerte');
    }
  };

  // Création d'une alerte de test
  const createTestAlert = async () => {
    try {
      await axios.post(`${BACKEND_URL}/monitoring/test-alert`, {}, {
        headers: getAuthHeaders()
      });
      
      // Recharger les alertes
      await loadAlerts();
      
      alert('Alerte de test créée avec succès');
    } catch (error) {
      console.error('Erreur création alerte test:', error);
      alert('Erreur lors de la création de l\'alerte de test');
    }
  };

  // Chargement initial et rafraîchissement
  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadSystemHealth(),
        loadDashboardMetrics(),
        loadAlerts(),
        loadLogs(),
        loadPerformanceData()
      ]);
    } catch (error) {
      console.error('Erreur chargement données monitoring:', error);
      setError('Erreur lors du chargement des données de monitoring');
    } finally {
      setLoading(false);
    }
  };

  // Effect pour chargement initial
  useEffect(() => {
    loadAllData();
  }, []);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      loadAllData();
    }, 30000); // Refresh toutes les 30 secondes

    setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  // Fonction utilitaire pour formater les dates
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Fonction utilitaire pour la couleur du niveau de log
  const getLogLevelColor = (level) => {
    const colors = {
      'DEBUG': '#6c757d',
      'INFO': '#17a2b8',
      'WARNING': '#ffc107',
      'ERROR': '#dc3545',
      'CRITICAL': '#721c24'
    };
    return colors[level] || '#6c757d';
  };

  // Fonction utilitaire pour la couleur de statut de santé
  const getHealthStatusColor = (status) => {
    const colors = {
      'EXCELLENT': '#28a745',
      'GOOD': '#6f42c1',
      'WARNING': '#ffc107',
      'CRITICAL': '#dc3545',
      'UNKNOWN': '#6c757d'
    };
    return colors[status] || '#6c757d';
  };

  // Rendu du contenu selon l'onglet actif
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'health':
        return renderHealthTab();
      case 'dashboard':
        return renderDashboardTab();
      case 'alerts':
        return renderAlertsTab();
      case 'logs':
        return renderLogsTab();
      case 'performance':
        return renderPerformanceTab();
      default:
        return <div>Onglet non reconnu</div>;
    }
  };

  // Rendu de l'onglet santé système
  const renderHealthTab = () => {
    if (!systemHealth) return <div>Chargement des données de santé...</div>;

    return (
      <div className="monitoring-health-tab">
        {/* Score de santé global */}
        <div className="health-score-container">
          <div className="health-score-card">
            <h3>Score de Santé Global</h3>
            <div 
              className="health-score-circle"
              style={{ 
                backgroundColor: getHealthStatusColor(systemHealth.health_status),
                color: 'white'
              }}
            >
              {systemHealth.health_score}%
            </div>
            <div className="health-status">
              Statut: <span style={{ color: getHealthStatusColor(systemHealth.health_status) }}>
                {systemHealth.health_status}
              </span>
            </div>
          </div>
        </div>

        {/* Métriques détaillées */}
        <div className="health-metrics-grid">
          <div className="metric-card">
            <h4>📊 Logs Dernière Heure</h4>
            <div className="metric-value">{systemHealth.metrics?.logs_last_hour || 0}</div>
          </div>
          
          <div className="metric-card">
            <h4>❌ Erreurs Dernière Heure</h4>
            <div className="metric-value error">{systemHealth.metrics?.errors_last_hour || 0}</div>
          </div>
          
          <div className="metric-card">
            <h4>📈 Erreurs Dernier Jour</h4>
            <div className="metric-value warning">{systemHealth.metrics?.errors_last_day || 0}</div>
          </div>
          
          <div className="metric-card">
            <h4>🚨 Alertes Actives</h4>
            <div className="metric-value critical">{systemHealth.metrics?.active_alerts || 0}</div>
          </div>
        </div>

        {/* Composants problématiques */}
        {systemHealth.problematic_components && systemHealth.problematic_components.length > 0 && (
          <div className="problematic-components">
            <h4>🔥 Composants les Plus Problématiques</h4>
            <div className="components-list">
              {systemHealth.problematic_components.map((comp, index) => (
                <div key={index} className="component-item">
                  <span className="component-name">{comp._id}</span>
                  <span className="error-count">{comp.count} erreurs</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommandations */}
        {systemHealth.recommendations && systemHealth.recommendations.length > 0 && (
          <div className="health-recommendations">
            <h4>💡 Recommandations</h4>
            <ul>
              {systemHealth.recommendations.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'onglet métriques dashboard
  const renderDashboardTab = () => {
    if (!dashboardMetrics) return <div>Chargement des métriques dashboard...</div>;

    return (
      <div className="monitoring-dashboard-tab">
        {/* Statut global dashboard */}
        <div className="dashboard-overall-status">
          <h3>Statut Global Dashboard</h3>
          <div 
            className="overall-status-badge"
            style={{ 
              backgroundColor: getHealthStatusColor(dashboardMetrics.overall_health),
              color: 'white'
            }}
          >
            {dashboardMetrics.overall_health}
          </div>
          <p>Dernière mise à jour: {formatTimestamp(dashboardMetrics.timestamp)}</p>
        </div>

        {/* Métriques par composant */}
        <div className="dashboard-components-grid">
          {Object.entries(dashboardMetrics.metrics_by_component || {}).map(([component, metrics]) => (
            <div key={component} className="component-metrics-card">
              <h4>{component.replace('dashboard_', '').replace('_', ' ')}</h4>
              
              <div className="component-metrics">
                <div className="metric-row">
                  <span>Requêtes (1h):</span>
                  <span>{metrics.requests_last_hour}</span>
                </div>
                
                <div className="metric-row">
                  <span>Erreurs (1h):</span>
                  <span className="error">{metrics.errors_last_hour}</span>
                </div>
                
                <div className="metric-row">
                  <span>Taux d'erreur:</span>
                  <span className={metrics.error_rate_percent > 5 ? 'critical' : 'normal'}>
                    {metrics.error_rate_percent.toFixed(1)}%
                  </span>
                </div>
                
                <div className="metric-row">
                  <span>Temps réponse moy:</span>
                  <span className={metrics.avg_response_time_seconds > 3 ? 'warning' : 'normal'}>
                    {metrics.avg_response_time_seconds}s
                  </span>
                </div>
                
                <div className="metric-row">
                  <span>Statut:</span>
                  <span 
                    className="status-badge"
                    style={{ color: getHealthStatusColor(metrics.health_status) }}
                  >
                    {metrics.health_status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Statut Circuit Breakers */}
        <div className="circuit-breakers-section">
          <h4>🔄 Circuit Breakers</h4>
          <div className="circuit-breakers-grid">
            {Object.entries(dashboardMetrics.circuit_breaker_status || {}).map(([endpoint, status]) => (
              <div key={endpoint} className="circuit-breaker-card">
                <h5>{endpoint.replace('_', ' ')}</h5>
                <div className="breaker-status">
                  <span 
                    className={`breaker-state ${status.state.toLowerCase()}`}
                    style={{ 
                      color: status.state === 'CLOSED' ? '#28a745' : 
                             status.state === 'OPEN' ? '#dc3545' : '#ffc107'
                    }}
                  >
                    {status.state}
                  </span>
                  <span className="failure-count">Échecs: {status.failure_count}</span>
                  {status.last_failure && (
                    <span className="last-failure">
                      Dernier échec: {formatTimestamp(status.last_failure)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommandations */}
        {dashboardMetrics.recommendations && dashboardMetrics.recommendations.length > 0 && (
          <div className="dashboard-recommendations">
            <h4>💡 Recommandations Dashboard</h4>
            <ul>
              {dashboardMetrics.recommendations.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'onglet alertes
  const renderAlertsTab = () => {
    return (
      <div className="monitoring-alerts-tab">
        <div className="alerts-header">
          <h3>🚨 Alertes Système</h3>
          <button 
            className="btn btn-primary"
            onClick={createTestAlert}
          >
            Créer Alerte Test
          </button>
        </div>

        {alerts.length === 0 ? (
          <div className="no-alerts">
            <p>✅ Aucune alerte active</p>
          </div>
        ) : (
          <div className="alerts-list">
            {alerts.map((alert, index) => (
              <div key={index} className={`alert-card ${alert.status.toLowerCase()}`}>
                <div className="alert-header">
                  <span className="alert-type">{alert.type}</span>
                  <span className="alert-timestamp">{formatTimestamp(alert.timestamp)}</span>
                  {alert.status === 'ACTIVE' && (
                    <button 
                      className="btn btn-sm btn-success"
                      onClick={() => resolveAlert(alert.id)}
                    >
                      Résoudre
                    </button>
                  )}
                </div>
                
                <div className="alert-message">{alert.message}</div>
                
                {alert.context && Object.keys(alert.context).length > 0 && (
                  <div className="alert-context">
                    <strong>Contexte:</strong>
                    <pre>{JSON.stringify(alert.context, null, 2)}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'onglet logs
  const renderLogsTab = () => {
    return (
      <div className="monitoring-logs-tab">
        <div className="logs-header">
          <h3>📋 Logs Système (50 derniers)</h3>
          <button 
            className="btn btn-secondary"
            onClick={loadLogs}
          >
            Actualiser
          </button>
        </div>

        {logs.length === 0 ? (
          <div className="no-logs">
            <p>Aucun log disponible</p>
          </div>
        ) : (
          <div className="logs-container">
            {logs.map((log, index) => (
              <div key={index} className="log-entry">
                <div className="log-header">
                  <span 
                    className="log-level"
                    style={{ backgroundColor: getLogLevelColor(log.level) }}
                  >
                    {log.level}
                  </span>
                  <span className="log-timestamp">{formatTimestamp(log.timestamp)}</span>
                  <span className="log-component">{log.component}</span>
                </div>
                
                <div className="log-event">{log.event}</div>
                
                {log.details && Object.keys(log.details).length > 0 && (
                  <div className="log-details">
                    <pre>{JSON.stringify(log.details, null, 2)}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Rendu de l'onglet performance
  const renderPerformanceTab = () => {
    if (!performanceData) return <div>Chargement des données de performance...</div>;

    return (
      <div className="monitoring-performance-tab">
        <div className="performance-header">
          <h3>⚡ Performance des Composants (24h)</h3>
          <div className="performance-summary">
            <span>Composants surveillés: {performanceData.summary?.components_monitored || 0}</span>
            <span>Points de données: {performanceData.summary?.data_points_collected || 0}</span>
          </div>
        </div>

        {Object.keys(performanceData.performance_data || {}).length === 0 ? (
          <div className="no-performance-data">
            <p>Aucune donnée de performance disponible</p>
          </div>
        ) : (
          <div className="performance-components">
            {Object.entries(performanceData.performance_data || {}).map(([component, data]) => (
              <div key={component} className="performance-component-card">
                <h4>{component}</h4>
                
                {/* Temps de réponse */}
                <div className="performance-section">
                  <h5>Temps de Réponse</h5>
                  <div className="performance-metrics-list">
                    {data.response_times?.slice(-10).map((entry, index) => (
                      <div key={index} className="performance-entry">
                        <span className="time">{entry.timestamp.split('-').pop()}h</span>
                        <span className="value">Moy: {entry.avg_response_time}s</span>
                        <span className="value">Max: {entry.max_response_time}s</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Nombre de requêtes */}
                <div className="performance-section">
                  <h5>Requêtes par Heure</h5>
                  <div className="performance-metrics-list">
                    {data.request_counts?.slice(-10).map((entry, index) => (
                      <div key={index} className="performance-entry">
                        <span className="time">{entry.timestamp.split('-').pop()}h</span>
                        <span className="value">{entry.count} requêtes</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Taux d'erreur */}
                <div className="performance-section">
                  <h5>Taux d'Erreur (%)</h5>
                  <div className="performance-metrics-list">
                    {data.error_rates?.slice(-10).map((entry, index) => (
                      <div key={index} className="performance-entry">
                        <span className="time">{entry.timestamp.split('-').pop()}h</span>
                        <span 
                          className={`value ${entry.rate > 5 ? 'critical' : entry.rate > 1 ? 'warning' : 'normal'}`}
                        >
                          {entry.rate}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="monitoring-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Chargement du tableau de bord de monitoring...</p>
      </div>
    );
  }

  return (
    <div className="monitoring-dashboard-container">
      {/* Header */}
      <div className="monitoring-header">
        <h1>🎯 Dashboard de Monitoring - Baobab Connect</h1>
        <div className="monitoring-actions">
          <button 
            className="btn btn-outline-primary"
            onClick={loadAllData}
            disabled={loading}
          >
            🔄 Actualiser
          </button>
          <div className="auto-refresh-indicator">
            ⏱️ Actualisation automatique: 30s
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger monitoring-error">
          {error}
        </div>
      )}

      {/* Navigation par onglets */}
      <div className="monitoring-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`monitoring-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Contenu de l'onglet actif */}
      <div className="monitoring-content">
        {renderActiveTabContent()}
      </div>

      {/* Styles CSS intégrés pour simplifier le déploiement */}
      <style jsx>{`
        .monitoring-dashboard-container {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .monitoring-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding: 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 10px;
        }

        .monitoring-actions {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .auto-refresh-indicator {
          font-size: 0.9em;
          opacity: 0.8;
        }

        .monitoring-tabs {
          display: flex;
          gap: 2px;
          margin-bottom: 25px;
          background: #f8f9fa;
          border-radius: 8px;
          padding: 4px;
        }

        .monitoring-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: transparent;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .monitoring-tab:hover {
          background: #e9ecef;
        }

        .monitoring-tab.active {
          background: #007bff;
          color: white;
        }

        .monitoring-content {
          background: white;
          border-radius: 10px;
          padding: 25px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          min-height: 600px;
        }

        .health-score-container {
          text-align: center;
          margin-bottom: 30px;
        }

        .health-score-card {
          display: inline-block;
          padding: 25px;
          background: #f8f9fa;
          border-radius: 15px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .health-score-circle {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5em;
          font-weight: bold;
          margin: 15px auto;
        }

        .health-status {
          margin-top: 15px;
          font-size: 1.2em;
          font-weight: 600;
        }

        .health-metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .metric-card {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 10px;
          text-align: center;
          border-left: 4px solid #007bff;
        }

        .metric-value {
          font-size: 2.5em;
          font-weight: bold;
          color: #007bff;
          margin: 10px 0;
        }

        .metric-value.error {
          color: #dc3545;
        }

        .metric-value.warning {
          color: #ffc107;
        }

        .metric-value.critical {
          color: #721c24;
        }

        .problematic-components, .health-recommendations {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 10px;
          padding: 20px;
          margin: 20px 0;
        }

        .components-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 15px;
        }

        .component-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: white;
          border-radius: 5px;
          border-left: 4px solid #dc3545;
        }

        .dashboard-overall-status {
          text-align: center;
          margin-bottom: 30px;
          padding: 25px;
          background: #f8f9fa;
          border-radius: 15px;
        }

        .overall-status-badge {
          display: inline-block;
          padding: 10px 25px;
          border-radius: 25px;
          font-weight: bold;
          margin: 15px;
        }

        .dashboard-components-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .component-metrics-card {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 10px;
          border-left: 4px solid #28a745;
        }

        .component-metrics {
          margin-top: 15px;
        }

        .metric-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
        }

        .metric-row:last-child {
          border-bottom: none;
        }

        .circuit-breakers-section {
          margin: 30px 0;
        }

        .circuit-breakers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
        }

        .circuit-breaker-card {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 10px;
          border-left: 4px solid #6f42c1;
        }

        .breaker-status {
          margin-top: 15px;
        }

        .breaker-state {
          font-weight: bold;
          padding: 5px 10px;
          border-radius: 5px;
          background: #e9ecef;
        }

        .alerts-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
        }

        .alerts-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .alert-card {
          padding: 20px;
          border-radius: 10px;
          border-left: 4px solid #ffc107;
        }

        .alert-card.active {
          background: #fff3cd;
          border-left-color: #dc3545;
        }

        .alert-card.resolved {
          background: #d4edda;
          border-left-color: #28a745;
        }

        .alert-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .alert-type {
          font-weight: bold;
          color: #dc3545;
        }

        .alert-message {
          margin: 15px 0;
          font-weight: 500;
        }

        .alert-context {
          margin-top: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 5px;
        }

        .logs-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
        }

        .logs-container {
          max-height: 600px;
          overflow-y: auto;
          border: 1px solid #e9ecef;
          border-radius: 10px;
        }

        .log-entry {
          padding: 15px;
          border-bottom: 1px solid #e9ecef;
          font-family: 'Courier New', monospace;
          font-size: 0.9em;
        }

        .log-entry:last-child {
          border-bottom: none;
        }

        .log-header {
          display: flex;
          gap: 15px;
          align-items: center;
          margin-bottom: 8px;
        }

        .log-level {
          color: white;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 0.8em;
          font-weight: bold;
          min-width: 70px;
          text-align: center;
        }

        .log-timestamp {
          color: #6c757d;
          font-size: 0.85em;
        }

        .log-component {
          color: #007bff;
          font-weight: bold;
        }

        .log-event {
          margin: 10px 0;
          line-height: 1.4;
        }

        .log-details {
          background: #f8f9fa;
          padding: 10px;
          border-radius: 5px;
          margin-top: 10px;
        }

        .log-details pre {
          margin: 0;
          font-size: 0.8em;
          max-height: 200px;
          overflow-y: auto;
        }

        .performance-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
        }

        .performance-summary {
          display: flex;
          gap: 20px;
          font-size: 0.9em;
          color: #6c757d;
        }

        .performance-components {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 25px;
        }

        .performance-component-card {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 10px;
          border-left: 4px solid #17a2b8;
        }

        .performance-section {
          margin: 20px 0;
        }

        .performance-section h5 {
          margin-bottom: 15px;
          color: #495057;
          font-size: 1.1em;
        }

        .performance-metrics-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 300px;
          overflow-y: auto;
        }

        .performance-entry {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background: white;
          border-radius: 5px;
          font-size: 0.9em;
        }

        .performance-entry .time {
          font-weight: bold;
          color: #6c757d;
          min-width: 60px;
        }

        .performance-entry .value {
          color: #495057;
        }

        .performance-entry .value.warning {
          color: #ffc107;
          font-weight: bold;
        }

        .performance-entry .value.critical {
          color: #dc3545;
          font-weight: bold;
        }

        .performance-entry .value.normal {
          color: #28a745;
        }

        .monitoring-dashboard-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 400px;
          text-align: center;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .monitoring-error {
          margin: 20px 0;
        }

        .btn {
          padding: 8px 16px;
          border: 1px solid transparent;
          border-radius: 5px;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          text-align: center;
          font-size: 0.9em;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .btn-primary {
          color: white;
          background-color: #007bff;
          border-color: #007bff;
        }

        .btn-primary:hover {
          background-color: #0056b3;
          border-color: #0056b3;
        }

        .btn-outline-primary {
          color: #007bff;
          background-color: transparent;
          border-color: #007bff;
        }

        .btn-outline-primary:hover {
          color: white;
          background-color: #007bff;
        }

        .btn-secondary {
          color: white;
          background-color: #6c757d;
          border-color: #6c757d;
        }

        .btn-success {
          color: white;
          background-color: #28a745;
          border-color: #28a745;
        }

        .btn-sm {
          padding: 5px 10px;
          font-size: 0.8em;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .no-alerts, .no-logs, .no-performance-data {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }

        @media (max-width: 768px) {
          .monitoring-tabs {
            flex-wrap: wrap;
          }
          
          .monitoring-tab {
            flex: 1;
            min-width: 120px;
          }
          
          .tab-label {
            display: none;
          }
          
          .health-metrics-grid,
          .dashboard-components-grid,
          .performance-components {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default MonitoringDashboard;